import React, { Component } from "react";

import FullCalendar from "@fullcalendar/react";
import interactionPlugin from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from '@fullcalendar/list';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';

import { connect } from 'react-redux';
import PageWrapper from "../PageWrapper/PageWrapper";
import { WithTranslation, withTranslation } from "react-i18next";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import MeetingsAPI, { Meeting, MeetingSmsUseCase, FREE_SMS_LIMIT_REACHED_CODE, FreeSmsLimitReachedResponse } from "../../api/MeetingsAPI";
import Legend from "../../components/Legend/Legend";
import { getMeetingBorderColor, getMeetingColor } from "../../shared/meetingTypeToColor";
import DropSelect from "../../components/DropSelect/DropSelect";
import { Modal } from "../../components/Modal/Modal";
import { Autocomplete } from "../../components/Autocomplete/Autocomplete";
import { CancelToken } from "../../api/SimpleAPIClient";
import CalendarFooter from "../../components/CalendarFooter/CalendarFooter";
import MeetingFormWrapper from "../../components/MeetingFormWrapper/MeetingFormWrapper";
import { FormType } from "../../shared/form.type";
import deleteIcon from "../../assets/images/bin.svg";
import IconButton from "../../components/IconButton/IconButton";
import ConfirmationPopup from "../../components/ConfirmationPopup/ConfirmationPopup";
import { Icon } from "../../components/Icon/Icon";
import BlockedDatesModal from "../../components/BlockedDatesModal/BlockedDatesModal";
import SelectInput from "../../components/SelectInput/SelectInput";
import { toastService } from "../../services/ToastService";
import EmployeesAPI, { Employee } from "../../api/EmployeesAPI";
import Card from "../../components/Card/Card";
import { PermissionFlags } from "../../components/PermissionFlags/PermissionFlags";
import DangerPrimaryButton from "../../components/DangerPrimaryButton/DangerPrimaryButton";
import { meetingTypeToTranslationKey } from "../../shared/meetingTypeToTranslationKey";
import { BolderText } from "../../components/BolderText/BolderText";
import Tooltip from "../../components/Tooltip/Tooltip";
import { parseDateTime } from "../../shared/timeUtils";
import usePermissionFlag from "../../hooks/usePermissionFlag";
import CONFIG from "../../config/config";
import { Settings } from "../../store/reducers/AccountSlice";
import Subtitle from "../../components/Subtitle/Subtitle";
import { AdminRestricted } from "../../components/AdminRestricted/AdminRestricted";
import PlainTextButton from "../../components/PlainTextButton/PlainTextButton";
import { Clock, Mail, Pencil, Phone, User } from "lucide-react";
import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import Collapsable from "../../components/Collapsable/Collapsable";
import MeetingSearchResult from "../../components/MeetingSearchResult/MeetingSearchResult";
import SlideOver from "../../components/SlideOver/SlideOver";
import SaleFormSteps from "../../components/SaleFormSteps/SaleFormSteps";
import { Sale } from "../../components/SalesSegment/SalesSegment";
import { datadogRum } from "@datadog/browser-rum";
import { shouldOfferSms, markDontAskUntilNextMonth } from "../../utils/smsConfirmStorage";
import SmsConfirmModal from "../../components/SmsConfirmModal/SmsConfirmModal";
import SmsLimitReachedModal from "../../components/SmsLimitReachedModal/SmsLimitReachedModal";

interface State {
  events: any[];
  view: string;
  selectedEvent: Meeting | null;
  calendarApi: any;
  currentViewName: string;
  searchIsLoading: boolean;
  action: FormType;
  showBlockedDates: boolean;
  signers: Employee[];
  showOverlapWarning: boolean;
  removedMeetings: Meeting[];
  showRemovedMeetingsModal: boolean;
  showExternalBookingConfirmation: boolean;
  showExternalBookingUpdateConfirmation: boolean;
  pendingUpdateMeeting?: Meeting;
  pendingDeleteInfo?: any;
  pendingEmployeeName?: string;
  originalStartTime?: string;
  originalEndTime?: string;
  selectedMonth: string;
  showSalesFlow: boolean;
  currentSale: any;
  salesFlowNavigation?: {
    currentStep: number;
    totalSteps: number;
    canProceed: () => boolean;
    handleNext: () => void;
    handleBack: () => void;
    buttonLabel?: string;
  };
  showSmsConfirmModal: boolean;
  showSmsLimitModal: boolean;
  smsPendingForSend: { meetingId: number; useCase: MeetingSmsUseCase } | null;
}

type BusinessHoursInput = Pick<
  Settings,
  | 'openingHours' | 'closingHours'
  | 'mondayOpeningHours' | 'mondayClosingHours' | 'mondayClosed'
  | 'tuesdayOpeningHours' | 'tuesdayClosingHours' | 'tuesdayClosed'
  | 'wednesdayOpeningHours' | 'wednesdayClosingHours' | 'wednesdayClosed'
  | 'thursdayOpeningHours' | 'thursdayClosingHours' | 'thursdayClosed'
  | 'fridayOpeningHours' | 'fridayClosingHours' | 'fridayClosed'
  | 'saturdayOpeningHours' | 'saturdayClosingHours' | 'saturdayClosed'
  | 'sundayOpeningHours' | 'sundayClosingHours' | 'sundayClosed'
>;

type BusinessHours = {
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
};

/** Normalize time to HH:mm for FullCalendar (strips optional seconds). */
function toTimeString(value: string): string {
  if (!value || typeof value !== 'string') return value;
  const trimmed = value.trim();
  return trimmed.length > 5 ? trimmed.slice(0, 5) : trimmed;
}

/**
 * Maps the business hours from the config to the business hours format expected by FullCalendar.
 * Uses per-day hours when available (e.g. mondayOpeningHours); otherwise falls back to
 * openingHours/closingHours for Monday–Saturday.
 */
function mapBusinessHours(config: BusinessHoursInput | null | undefined): BusinessHours[] {
  if (!config) return [];

  const daysMap = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  } as const;

  const businessHours: Record<string, BusinessHours> = {};

  (Object.keys(daysMap) as Array<keyof typeof daysMap>).forEach((day) => {
    const dayOpeningHours = config[`${day}OpeningHours`];
    const dayClosingHours = config[`${day}ClosingHours`];
    const dayClosed = config[`${day}Closed`];

    if (!dayClosed && typeof dayOpeningHours === 'string' && typeof dayClosingHours === 'string') {
      const start = toTimeString(dayOpeningHours);
      const end = toTimeString(dayClosingHours);
      if (!start || !end) return;
      const timeKey = `${start}-${end}`;
      if (!businessHours[timeKey]) {
        businessHours[timeKey] = {
          daysOfWeek: [],
          startTime: start,
          endTime: end,
        };
      }
      businessHours[timeKey].daysOfWeek.push(daysMap[day]);
    }
  });

  const result = Object.values(businessHours);

  if (result.length > 0) return result;

  const fallbackStart = config.openingHours && toTimeString(config.openingHours);
  const fallbackEnd = config.closingHours && toTimeString(config.closingHours);
  if (fallbackStart && fallbackEnd) {
    return [
      {
        daysOfWeek: [1, 2, 3, 4, 5, 6],
        startTime: fallbackStart,
        endTime: fallbackEnd,
      },
    ];
  }

  return [];
}

const emptyEvent: Meeting = {
  customerName: '',
  customerSurname: '',
  startTime: new Date().toISOString(),
  endTime: new Date().toISOString(),
  type: 'VISIT',
  reminder: false,
  disable: false,
  weddingDate: null,
  phone: null,
  lastUpdateTime: null,
  notes: null,
  title: null
}

const isAllDay = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const startHour = startDate.getUTCHours();
  const endHour = endDate.getUTCHours();

  return startHour <= 7 && endHour >= 18;
};

const mapStateToProps = (state: any) => ({
  settings: state.settings.value,
});

interface CalendarPageOwnProps {
  hasAccess?: boolean;
}

type CalendarPageProps = CalendarPageOwnProps & StateProps & WithTranslation;

const CalendarPageWrapper: React.FC<CalendarPageProps> = (props) => {
  const { hasAccess } = usePermissionFlag(['create-meetings']);

  return <CalendarPage {...props} hasAccess={hasAccess} />;
};

interface StateProps {
  settings: Settings;
}

class CalendarPage extends Component<CalendarPageProps & StateProps, State> {
  calendarRef = React.createRef<any>();

  static defaultProps = {
    hasAccess: false
  };

  state: State = {
    events: [],
    view: "dayGridMonth",
    selectedEvent: null,
    calendarApi: null,
    currentViewName: "dayGridMonth",
    searchIsLoading: false,
    action: 'READ',
    showBlockedDates: false,
    signers: [],
    showOverlapWarning: false,
    removedMeetings: [],
    showRemovedMeetingsModal: false,
    showExternalBookingConfirmation: false,
    showExternalBookingUpdateConfirmation: false,
    pendingUpdateMeeting: undefined,
    selectedMonth: new Date().toISOString().slice(0, 7),
    showSalesFlow: false,
    currentSale: null,
    salesFlowNavigation: undefined,
    showSmsConfirmModal: false,
    showSmsLimitModal: false,
    smsPendingForSend: null,
  };

  search = (input: string, resultsSize: number, setResultsFn: any) => {
    this.setState({ searchIsLoading: true });
    MeetingsAPI.search(input).then((resp: any) => {
      const raw = resp.data;
      const respRaw = raw.slice(0, resultsSize);
      this.setState({ searchIsLoading: false });
      setResultsFn(respRaw);
    });
    return CancelToken.source().cancel("Cancelling...");
  };

  closeRemovedMeetingsModal = () => {
    this.setState({ showRemovedMeetingsModal: false });
  };

  showMeeting = (meeting: Meeting, action?: FormType) => {
    this.goToDay(Date.parse(meeting.startTime));
    
    if (this.props.hasAccess) {
      this.setState({ action: "UPDATE" });
    } else {
      this.setState({ action: action ? action : "READ" });
    }

    this.setState({ 
      selectedEvent: meeting,
      originalStartTime: meeting.startTime,
      originalEndTime: meeting.endTime
    });
  }

  handleNewButtonClick = () => {
    this.setState({ showBlockedDates: true });
  }

  handleOpenSalesFlow = () => {
    const newSale: Sale = {
      saleDate: new Date().toISOString().split('T')[0],
      clientName: '',
      customerName: '',
      dressName: '',
      dressDescription: '',
      totalPrice: 0,
      currency: 'EUR',
      paymentStatus: 'UNPAID',
      status: 'JUST_SIGNED',
      receiptNumber: '',
      instalments: []
    };
    this.setState({ showSalesFlow: true, currentSale: newSale, salesFlowNavigation: undefined });
  }

  handleCloseSalesFlow = () => {
    this.setState({ showSalesFlow: false, currentSale: null, salesFlowNavigation: undefined });
  }

  handleSalesFlowNavigationChange = (navigation: {
    currentStep: number;
    totalSteps: number;
    canProceed: () => boolean;
    handleNext: () => void;
    handleBack: () => void;
    buttonLabel?: string;
  }) => {
    this.setState({ salesFlowNavigation: navigation });
  }

  handleSaleChange = (sale: Sale) => {
    this.setState({ currentSale: sale });
  }

  handleCompleteSale = () => {
    const { currentSale } = this.state;
    if (!currentSale) return;

    // Sale is always created in step 4 (Register Sale) in SaleFormSteps; we only close the flow.
    this.handleCloseSalesFlow();
  }

  confirmDeleteWithBookingCheck = async (info: any, employeeName?: string) => {
    const meetingId = info.event.extendedProps.id;
    try {
      const isExternalBooking = (await MeetingsAPI.checkBooking(meetingId)).data;
      if (isExternalBooking) {
        this.setState({
          showExternalBookingConfirmation: true,
          pendingDeleteInfo: info,
          pendingEmployeeName: employeeName,
        });
      } else {
        this.handleEventDelete(info, employeeName);
      }
    } catch (error) {
      console.error(error);
    }
  };

  handleConfirmBookingUpdate = async () => {
    if (this.state.pendingUpdateMeeting) {
      const saved = await this.finalizeMeetingRegistration(this.state.pendingUpdateMeeting);
      this.setState({
        showExternalBookingUpdateConfirmation: false,
        pendingUpdateMeeting: undefined,
      });
      if (saved?.phone && shouldOfferSms()) {
        this.setState({
          showSmsConfirmModal: true,
          smsPendingForSend: { meetingId: saved.id!, useCase: 'time_update' },
        });
      }
    } else {
      this.setState({
        showExternalBookingUpdateConfirmation: false,
        pendingUpdateMeeting: undefined,
      });
    }
  };
  
  handleCancelBookingUpdate = () => {
    this.setState({ 
      showExternalBookingUpdateConfirmation: false, 
      pendingUpdateMeeting: undefined 
    });
  };

  handleConfirmBookingDeletion = () => {
    const { pendingDeleteInfo, pendingEmployeeName } = this.state;
    if (pendingDeleteInfo) {
      this.handleEventDelete(pendingDeleteInfo, pendingEmployeeName);
    }
    this.setState({ showExternalBookingConfirmation: false, pendingDeleteInfo: undefined, pendingEmployeeName: undefined });
  };

  handleCancelBookingDeletion = () => {
    this.setState({ showExternalBookingConfirmation: false, pendingDeleteInfo: undefined, pendingEmployeeName: undefined });
  };

  handleSmsConfirmYes = async () => {
    const { smsPendingForSend } = this.state;
    if (!smsPendingForSend) return;
    try {
      await MeetingsAPI.sendSms(smsPendingForSend.meetingId, smsPendingForSend.useCase);
      datadogRum.addAction('Meeting SMS Sent', {
        useCase: smsPendingForSend.useCase,
        meetingId: smsPendingForSend.meetingId,
        brandName: this.props.settings.tenantName ?? undefined,
      });
      toastService.showToast(this.props.t('toast.success.sms_sent'), 'success');
      this.setState({ showSmsConfirmModal: false, smsPendingForSend: null });
    } catch (error: unknown) {
      const err = error as { response?: { status: number; data?: FreeSmsLimitReachedResponse } };
      if (err?.response?.status === 403 && err?.response?.data?.code === FREE_SMS_LIMIT_REACHED_CODE) {
        datadogRum.addAction('Meeting SMS - Limit Reached', {
          useCase: smsPendingForSend.useCase,
          brandName: this.props.settings.tenantName ?? undefined,
        });
        this.setState({ showSmsConfirmModal: false, smsPendingForSend: null, showSmsLimitModal: true });
      } else {
        toastService.showToast(this.props.t('toast.errors.generic'), 'error');
      }
    }
  };

  handleSmsConfirmNo = (dontAskUntilNextMonth: boolean) => {
    if (dontAskUntilNextMonth) {
      markDontAskUntilNextMonth();
    }
    this.setState({ showSmsConfirmModal: false, smsPendingForSend: null });
  };

  handleSmsLimitClose = (dontOfferUntilNextMonth: boolean) => {
    if (dontOfferUntilNextMonth) {
      markDontAskUntilNextMonth();
    }
    this.setState({ showSmsLimitModal: false });
  };

  // Renders events for time grid views (timeGridDay/timeGridWeek)
  renderTimeGridEvent = (info: any) => {
    const event = info.event;
    return (
      <div
        className="rounded pl-1 text-black hover:bg-blue-400 cursor-pointer min-h-full w-100"
        style={{
          backgroundColor: event.backgroundColor,
        }}
        onClick={() => this.handleEventClick(info)}
      >
        <span className="truncate flex items-center">
          <PermissionFlags requiredFlags={['remove-meetings']}>
            <div 
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
            >
              <IconButton>
                <div
                  onClick={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
                >
                  <ConfirmationPopup
                  onConfirm={(employeeEvent) =>
                    this.confirmDeleteWithBookingCheck(
                      info,
                      employeeEvent?.target?.value
                    )
                  }
                  confirmText={"global.confirm"}
                  title={"global.confirmation_popup.title"}
                  extraContent={
                    this.state.signers.length > 0
                      ? (setExtraState) => (
                          <SelectInput
                            values={this.state.signers.map((e) => ({
                              value: e.name,
                              readableValue: e.name,
                            }))}
                            onChange={setExtraState}
                            label={"calendar.meetings.removal.signature"}
                          />
                        )
                      : undefined
                  }
                >
                  <Icon url={deleteIcon} cssStyles="w-5" />
                </ConfirmationPopup>
                </div>
              </IconButton>
            </div>
          </PermissionFlags>
          <p><BolderText text={event.title} /></p>
        </span>
      </div>
    );
  }

  eventContent = (info: any, view: any) => {
    const event = info.event;

    if (event.extendedProps.isBlockedDate) {
      return (
        <div
          className="rounded pl-1 text-black hover:bg-blue-400 cursor-pointer min-h-full w-100"
          style={{
            backgroundColor: event.backgroundColor,
          }}
        >
          <span className="truncate flex items-end">
            <PermissionFlags requiredFlags={['remove-meetings'] || []}>
              <div className="fc-list-event-delete mr-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <IconButton>
                  <ConfirmationPopup
                      onConfirm={(employeeEvent) =>
                        this.confirmDeleteWithBookingCheck(
                          info,
                          employeeEvent?.target?.value
                        )
                      }
                      confirmText={"global.confirm"}
                      title={"global.confirmation_popup.title"}
                      extraContent={
                        this.state.signers.length > 0
                          ? (setExtraState) => (
                              <SelectInput
                                values={this.state.signers.map((e) => ({
                                  value: e.name,
                                  readableValue: e.name,
                                }))}
                                onChange={setExtraState}
                                label={"calendar.meetings.removal.signature"}
                              />
                            )
                          : undefined
                      }
                    >
                      <Icon url={deleteIcon} cssStyles="w-5" />
                  </ConfirmationPopup>
                </IconButton>
              </div>
            </PermissionFlags>
            <p><BolderText text={event.title} /></p>
          </span>
        </div>
      );
    } else {
      if (view === 'dayGridMonth') {
        return (
          <>
            <Tooltip content={event.title} >
              <div className="fc-daygrid-event-dot" style={{backgroundColor: event.borderColor}}></div>
            </Tooltip>
          </>
        );
      } else if (view === 'listDay') {
        // Custom list view layout
        const startTime = parseDateTime(event.start, this.props.settings.timezone).time;
        // Hack to fix - FullCalendar sets event.end to null if the event is all day
        const endTime = event.end ? parseDateTime(event.end, this.props.settings.timezone).time : 
                       (event.extendedProps?.endTime ? parseDateTime(event.extendedProps.endTime, this.props.settings.timezone).time : 'N/A');
        
        // Extract name and phone from event title or extended props
        const meeting = event.extendedProps;
        const customerName = meeting?.customerName || '';
        const customerSurname = meeting?.customerSurname || '';
        const phone = meeting?.phone || '';
        const displayName = `${customerName} ${customerSurname}`.trim();
        const displayPhone = phone ? ` - ${phone}` : '';
        
        return (
          <div 
            className="fc-list-event-mobile flex items-center w-full rounded pl-1 text-black hover:bg-blue-400 cursor-pointer min-h-full"
            style={{
              backgroundColor: event.backgroundColor,
              width: '100%',
              maxWidth: 'none',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
            onClick={() => this.handleEventClick(info)}
          >
            <PermissionFlags requiredFlags={['remove-meetings'] || []}>
              <div className="fc-list-event-delete mr-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <IconButton>
                  <ConfirmationPopup
                    onConfirm={(employeeEvent) =>
                      this.confirmDeleteWithBookingCheck(
                        info,
                        employeeEvent?.target?.value
                      )
                    }
                    confirmText={"global.confirm"}
                    title={"global.confirmation_popup.title"}
                    extraContent={
                      this.state.signers.length > 0
                        ? (setExtraState) => (
                            <SelectInput
                              values={this.state.signers.map((e) => ({
                                value: e.name,
                                readableValue: e.name,
                              }))}
                              onChange={setExtraState}
                              label={"calendar.meetings.removal.signature"}
                            />
                          )
                        : undefined
                    }
                  >
                    <Icon url={deleteIcon} cssStyles="w-4 h-4" />
                  </ConfirmationPopup>
                </IconButton>
              </div>
            </PermissionFlags>
            <div className="flex items-center flex-1 min-w-0">
              <div className="fc-list-event-time text-sm font-medium text-gray-600 mr-3 flex-shrink-0">
                {startTime} - {endTime}
              </div>
              <div className="fc-list-event-title flex-1 overflow-x-auto whitespace-nowrap min-w-0" style={{ 
                width: '100%', 
                maxWidth: 'none',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch'
              }}>
                <BolderText text={`${displayName}${displayPhone}`} />
              </div>
            </div>
          </div>
        );
      } else {
        return this.renderTimeGridEvent(info);
      }
    }
  }

  componentWillUnmount() {
    if (this.calendarRef.current) {
      const calendarApi = this.calendarRef.current.getApi();
      calendarApi.destroy();
    }
    window.removeEventListener('resize', this.handleResize);
  }

  async componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('openSalesFlow') === 'true') {
        this.handleOpenSalesFlow();

        params.delete('openSalesFlow');
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    } catch (e) {
      // Ignore URL parsing errors
    }

    const resp = await EmployeesAPI.getMeetingRemovalSigners();
    this.setState({ signers: resp.data });

    const now = new Date();
    const startOfMonth = new Date(
      now.getFullYear(),
      now.getMonth(),
      1
    ).toISOString();
    const endOfMonth = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0
    ).toISOString();

    this.fetchEvents(startOfMonth, endOfMonth);
  }

  handleResize = () => {
    const calendarApi = this.calendarRef.current.getApi();
    if (window.innerWidth < CONFIG.MOBILE_BREAKPOINT) {
        calendarApi.changeView('listDay');
        // Set height to fill available viewport space on mobile
        const availableHeight = window.innerHeight - 200; // Account for header and other UI elements
        calendarApi.setOption('height', availableHeight);
    } else {
        calendarApi.changeView('dayGridMonth');
        // Reset to auto height on desktop
        calendarApi.setOption('height', 'auto');
    }
    
    // Update button appearance based on screen size
    this.updateButtonAppearance();
  }

  getButtonContent = (buttonType: 'block' | 'refresh') => {
    if (window.innerWidth < CONFIG.MOBILE_BREAKPOINT) {
      // Return simple symbols for mobile
      if (buttonType === 'block') {
        return '✕';
      } else {
        return '↻';
      }
    }
    // Return text for desktop
    return buttonType === 'block' ? `${this.props.t("global.block")}` : `${this.props.t("global.refresh")}`;
  }

  updateButtonAppearance = () => {
    const calendarApi = this.calendarRef.current.getApi();
    
    // Update custom buttons with current content
    calendarApi.setOption('customButtons', {
      ...calendarApi.getOption('customButtons'),
      block: {
        text: this.getButtonContent('block'),
        click: this.handleNewButtonClick,
      },
      refresh: {
        text: this.getButtonContent('refresh'),
        click: this.handleRefresh,
      },
    });
    
    // Style the block button after a short delay to ensure it's rendered
    setTimeout(() => {
      this.styleBlockButton();
    }, 100);
  }

  styleBlockButton = () => {
    // Removed custom styling - let FullCalendar handle button appearance
  }

  fetchEvents = async (startDate: any, endDate: any) => {
    const { data: blockedDates } = await MeetingsAPI.getUnavailableDates(startDate, endDate);

    const { data: meetings } = await MeetingsAPI.get(startDate, endDate);

    const transformedEvents = [
      ...blockedDates.map((date: any) => ({
        id: date.id,
        start: date.start,
        title: this.props.t("calendar.blocked_dates"),
        end: date.end,
        rendering: "background",
        backgroundColor: "#ffe9e9",
        allDay: isAllDay(date.start, date.end),
        extendedProps: {
          isBlockedDate: true,
          id: date.id
        },
      })),
      ...meetings.map((meeting: any) => ({
        id: meeting.id,
        title: [
                meeting.customerName || '',
                meeting.customerSurname || '',
                meeting.phone ? `- ${meeting.phone}` : null,
                meeting.notes ? `(${meeting.notes})` : null,
                `- ${this.props.t(meetingTypeToTranslationKey(meeting.type))}`
               ].filter((p): p is string => Boolean(p && `${p}`.trim().length > 0)).join(' '),
        start: meeting.startTime,
        end: meeting.endTime,
        allDay: ['WAITING_LIST', 'NOTE'].includes(meeting.type),
        backgroundColor: getMeetingColor(meeting.type),
        borderColor: getMeetingBorderColor(meeting.type),
        extendedProps: {
          ...meeting,
        },
      })),
    ];

    this.setState({ events: transformedEvents });
  };

  handleMonthChange = async (e: any) => {
    const selectedDate = new Date(e.target.value);
    const calendarApi = this.calendarRef.current.getApi();

    this.setState({ selectedMonth: e.target.value });

    // Change the view to the selected month and year
    if (window.innerWidth < CONFIG.MOBILE_BREAKPOINT) {
      calendarApi.changeView('timeGridDay', selectedDate);
    } else {
        calendarApi.changeView('dayGridMonth', selectedDate);
    }

    // Fetch events for the new month
    const startOfMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      1
    ).toISOString();
    const endOfMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + 1,
      0
    ).toISOString();
    await this.fetchEvents(startOfMonth, endOfMonth);
  };

  handleEventDelete = async (info: any, employeeName?: string) => {
    if (info.event.extendedProps) {
      try {
        const meetingId = info.event.extendedProps.id;
        const phone = info.event.extendedProps.phone;
        if (info.event.extendedProps.isBlockedDate) {
          await MeetingsAPI.unblock(meetingId);
        } else {
          await MeetingsAPI.delete(meetingId, employeeName);
        }
        this.setState({ selectedEvent: null, action: "READ" });

        // Refetch events to update the calendar
        const calendarApi = this.calendarRef.current.getApi();
        const startDate = calendarApi.view.activeStart.toISOString();
        const endDate = calendarApi.view.activeEnd.toISOString();
        await this.fetchEvents(startDate, endDate);

        if (!info.event.extendedProps.isBlockedDate && phone && shouldOfferSms()) {
          this.setState({
            showSmsConfirmModal: true,
            smsPendingForSend: { meetingId, useCase: 'cancelation' },
          });
        }
      } catch (error) {
        toastService.showToast(this.props.t("toast.errors.remove_meeeting"), 'error');
      }
    }
  };

  handleEventClick = (info: any) => {
    if (!info.event.extendedProps.isBlockedDate) {
      if (this.props.hasAccess) {
        this.setState({ action: "UPDATE", originalStartTime: info.event.extendedProps?.startTime, originalEndTime: info.event.extendedProps?.endTime });
      }
      this.setState({ selectedEvent: info.event.extendedProps });
    }
  };

  fetchRemovedMeetings = async () => {
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    const formattedStartDate = startDate.toISOString();
    const endDate = new Date().toISOString();
  
    try {
      const { data: removedMeetings } = await MeetingsAPI.get(
        formattedStartDate,
        endDate,
        undefined,
        'REMOVED'
      );
      this.setState({ removedMeetings, showRemovedMeetingsModal: true });
    } catch (error) {
      toastService.showToast(this.props.t("global.error"), 'error');
    }
  };

  closeModal = () => {
    this.setState({ selectedEvent: null });
    this.setState({ action: "READ" });
  };

  isWeekOrDayView = (view: string) => {
    return view && (view === 'timeGridDay' || view === 'timeGridWeek');
  }

  handleDateClick = (arg: any) => {
    const clickViewType = arg.view.type;
    if (this.isWeekOrDayView(clickViewType) && this.props.hasAccess) {
      const emptyEventWithDate = { ...emptyEvent, startTime: arg.start.toISOString(), endTime: arg.end.toISOString() };
      this.setState({ action: 'CREATE' });
      this.setState({ selectedEvent: emptyEventWithDate });
    } else {
      const calendarApi = this.calendarRef.current.getApi();
      calendarApi.changeView("timeGridDay", arg.start);
      this.setState({ currentViewName: "timeGridDay" });
    }
  };
  
  handleToday = async () => {
    this.goToDay();
  }

  goToDay = async (date: number = Date.now()) => {
    const calendarApi = this.calendarRef.current.getApi();
    calendarApi.changeView("timeGridDay", date);
  }

  handleRefresh = async () => {
    const today = Date.now();
    this.handleMonthChange({ target: { value: today }});
  };

  handleDatesSet = async (arg: any) => {
    const startDate = arg.start.toISOString();
    const endDate = arg.end.toISOString();

    await this.fetchEvents(startDate, endDate);
  };

  refresh = async () => {
    const calendarApi = this.calendarRef.current.getApi();
    const startDate = calendarApi.view.activeStart.toISOString();
    const endDate = calendarApi.view.activeEnd.toISOString();
    await this.fetchEvents(startDate, endDate);
  }

  refreshAfterBlockDates = async () => {
    this.setState({ showBlockedDates: false }); 
    this.refresh();
  }

  finalizeMeetingRegistration = async (meeting: Meeting): Promise<Meeting | null> => {
    const action = meeting.id ? 'UPDATE' : this.state.action;

    if (action === 'CREATE') {
      const { data } = await MeetingsAPI.add(meeting);
      const saved: Meeting | null = Array.isArray(data) && data.length
        ? data[0]
        : (data && typeof data === 'object' && 'id' in data ? (data as unknown) as Meeting : null);
      this.goToDay(Date.parse(meeting.startTime));
      this.setState({ selectedEvent: null, action: "READ", showOverlapWarning: false });
      this.refresh();
      return saved ?? null;
    } else if (action === 'UPDATE') {
      await MeetingsAPI.update(meeting.id!, meeting);
      this.goToDay(Date.parse(meeting.startTime));
      this.setState({ selectedEvent: null, action: "READ", showOverlapWarning: false });
      this.refresh();
      return meeting;
    }
    return null;
  }

  render() {
    const { events, view, selectedEvent, searchIsLoading, action, showBlockedDates, showOverlapWarning, showRemovedMeetingsModal, removedMeetings, selectedMonth } = this.state;
    const { settings } = this.props;

    const checkForOverlap = async (meeting: Meeting) => {
      return MeetingsAPI.checkAvailability(meeting.startTime);
    }
    
    const registerAndClose = async (meeting: Meeting) => {
      if (!meeting) return;
    
      if (!meeting.endTime) {
        const saved = await this.finalizeMeetingRegistration(meeting);
        if (saved?.phone && shouldOfferSms()) {
          this.setState({
            showSmsConfirmModal: true,
            smsPendingForSend: { meetingId: saved.id!, useCase: meeting.id ? 'time_update' : 'new_appointment_booked' },
          });
        }
        return;
      }
    
      const now = new Date();
      const meetingEndTime = new Date(meeting.endTime);
    
      if (!isNaN(meetingEndTime.getTime()) && meetingEndTime < now) {
        const saved = await this.finalizeMeetingRegistration(meeting);
        if (saved?.phone && shouldOfferSms()) {
          this.setState({
            showSmsConfirmModal: true,
            smsPendingForSend: { meetingId: saved.id!, useCase: meeting.id ? 'time_update' : 'new_appointment_booked' },
          });
        }
        return;
      }
    
      const overlapExists = await (await checkForOverlap(meeting)).data.matches;
    
      if (overlapExists) {
        this.setState({ showOverlapWarning: true });
        return;
      }

      if (this.state.action === 'UPDATE' || meeting.id) {
        try {
          const isExternalBooking = (await MeetingsAPI.checkBooking(meeting.id!)).data;

          const oldStart = this.state.originalStartTime;
          const oldEnd = this.state.originalEndTime;
          const newStart = meeting.startTime;
          const newEnd = meeting.endTime;
          const dateTimeChanged = oldStart !== newStart || oldEnd !== newEnd;

          if (isExternalBooking && dateTimeChanged) {
            this.setState({
              showExternalBookingUpdateConfirmation: true,
              pendingUpdateMeeting: meeting,
            });
            return;
          }
        } catch (error) {
          console.error(error);
          return;
        }
      }

      const saved = await this.finalizeMeetingRegistration(meeting);
      if (saved?.phone && shouldOfferSms()) {
        this.setState({
          showSmsConfirmModal: true,
          smsPendingForSend: { meetingId: saved.id!, useCase: meeting.id ? 'time_update' : 'new_appointment_booked' },
        });
      }
    };
    
    const handleOverlapConfirm = async (e: Meeting | null) => {
      if (e) {
        const saved = await this.finalizeMeetingRegistration(e);
        if (saved?.phone && shouldOfferSms()) {
          this.setState({
            showSmsConfirmModal: true,
            smsPendingForSend: { meetingId: saved.id!, useCase: e.id ? 'time_update' : 'new_appointment_booked' },
          });
        }
      }
    }
    
    const handleOverlapCancel = () => {
      this.setState({ showOverlapWarning: false });
    }    

    const actionButton = (action: FormType) => {
      switch (action) {
        case 'CREATE':
          return <span className="flex justify-center"><PrimaryButton text={this.props.t("global.create")!} fn={() => registerAndClose(selectedEvent!)} /></span>;
        case 'UPDATE':
          return <span className="flex justify-center"><PrimaryButton text={this.props.t("global.update")!} fn={() => registerAndClose(selectedEvent!)} /></span>;
        default:
          return;
      }
    };

    const showNewMeeting = () => {
      this.setState({ action: 'CREATE' });
      this.setState({ selectedEvent: emptyEvent });
    };

    const overlapModal = this.state.showOverlapWarning && (
      <Modal modalTitle={"calendar.meetings.overlap_popup.title"} canClose onClose={handleOverlapCancel}>
        <>
          <p className="py-5">{ this.props.t("calendar.meetings.overlap_popup.body") }</p>
          <div className="flex justify-center mt-5">
            <DangerPrimaryButton text={this.props.t('global.confirm')!} fn={() => handleOverlapConfirm(selectedEvent)} />
          </div>
        </>
      </Modal>
    );

    return (
      <PageWrapper>
        <>
          <style>
            {`
              @media (max-width: 768px) {
                .fc-toolbar-chunk .fc-button {
                  transition: all 0.2s ease;
                }
                
                /* Hide dots and color circles in list view */
                .fc-list-event .fc-event-dot {
                  display: none !important;
                }
                
                .fc-list-event .fc-list-event-dot {
                  display: none !important;
                }
                
                /* Hide the default time display in list view */
                .fc-list-event-time {
                  display: none !important;
                }
                
                .fc-list-day-side {
                  display: none !important;
                }
                
                /* Enable horizontal scrolling for list events */
                .fc-list-event {
                  overflow-x: auto;
                  white-space: nowrap;
                }
                
                /* Style the custom list event layout */
                .fc-list-event-mobile {
                  padding: 8px 12px;
                  border-radius: 4px;
                  margin-bottom: 2px;
                  min-height: 48px;
                  overflow-x: auto;
                  -webkit-overflow-scrolling: touch;
                }
                
                .fc-list-event-time {
                  min-width: 90px;
                  color: #6b7280;
                  font-weight: 500;
                }
                
                .fc-list-event-title {
                  overflow-x: auto;
                  white-space: nowrap;
                  color: #111827;
                  font-weight: 500;
                  min-width: 0;
                  flex: 1;
                }
                
                /* Desktop styles for list view */
                @media (min-width: 769px) {
                  .fc-list-event-mobile {
                    padding: 12px 16px;
                    min-height: 56px;
                    width: 100%;
                    max-width: none;
                  }
                  
                  .fc-list-event-title {
                    font-size: 14px;
                    flex: 1;
                    min-width: 0;
                  }
                  
                  /* Make the list container expand to full width */
                  .fc-list-event {
                    width: 100%;
                    max-width: none;
                  }
                  
                  /* Ensure the list view container takes full width */
                  .fc-list-view {
                    width: 100%;
                  }
                  
                  /* Hide time display on desktop too */
                  .fc-list-event-time {
                    display: none !important;
                  }
                  
                  .fc-list-day-side {
                    display: none !important;
                  }
                }
                
                .fc-list-event-delete {
                  min-width: 32px;
                }
                
                /* Ensure proper touch interaction for time grid events */
                .fc-event .fc-event-main {
                  pointer-events: auto;
                }
                
                .fc-event .fc-event-main * {
                  pointer-events: auto;
                }
                
                /* Ensure delete button is touchable in time grid */
                .fc-event .fc-event-main button {
                  pointer-events: auto;
                  touch-action: manipulation;
                }
                
                /* Ensure touch events work for time grid events */
                .fc-event .fc-event-main {
                  touch-action: manipulation;
                }
                
                .fc-event .fc-event-main * {
                  touch-action: manipulation;
                }
                
                /* Specific styling for time grid delete buttons */
                .fc-event .fc-event-main button[data-dd-action-name] {
                  pointer-events: auto;
                  touch-action: manipulation;
                  z-index: 10;
                  position: relative;
                }
              }
            `}
          </style>
          <div className="space-y-2 md:space-y-5 flex flex-col md:block">
            <Card className="flex-shrink-0 md:flex-shrink">
              <>
                {/* Mobile: Collapsible calendar actions, Desktop: Always visible */}
                <div className="md:hidden">
                  <Collapsable 
                    textOnExpanded="calendar.actions.hide" 
                    textOnHiden="calendar.actions.show"
                  >
                    <div className="space-y-4 mt-4">
                      <div className="calendar-header flex flex-row justify-between items-center space-x-3">
                        <DropSelect compact onSelect={this.handleMonthChange} value={selectedMonth}/>
                        <Autocomplete
                          resultsSize={15}
                          searchFn={this.search}
                          onSelect={(m: Meeting) => this.showMeeting(m)}
                          placeholder={"calendar.meetings.search.placeholder"}
                          isLoading={searchIsLoading}
                          errorMessage={"global.empty_results"}
                          toElementFn={(m: Meeting) => (
                            <MeetingSearchResult
                              key={m.id}
                              meeting={m}
                              onView={(meeting) => this.showMeeting(meeting)}
                              timezone={settings.timezone}
                            />
                          )}
                        />
                      </div>
                      <CalendarFooter onNewFn={showNewMeeting} onOpenSalesFlow={this.handleOpenSalesFlow} />
                      <div className="self-start text-left">
                        <AdminRestricted>
                          <PlainTextButton text={this.props.t("calendar.import.cta")} onClick={() => window.location.href = '/settings'}/>
                        </AdminRestricted>
                      </div>
                    </div>
                  </Collapsable>
                </div>
                
                {/* Desktop: Always visible calendar actions */}
                <div className="hidden md:block">
                <div className="calendar-header flex-col md:flex md:flex-row justify-between items-center space-x-3">
                  <DropSelect compact onSelect={this.handleMonthChange} value={selectedMonth}/>
                  <Autocomplete
                    resultsSize={15}
                    searchFn={this.search}
                    onSelect={(m: Meeting) => this.showMeeting(m)}
                    placeholder={"calendar.meetings.search.placeholder"}
                    isLoading={searchIsLoading}
                    errorMessage={"global.empty_results"}
                    toElementFn={(m: Meeting) => (
                      <MeetingSearchResult
                        key={m.id}
                        meeting={m}
                        onView={(meeting) => this.showMeeting(meeting)}
                        timezone={settings.timezone}
                      />
                    )}
                  />
                </div>
                <CalendarFooter onNewFn={showNewMeeting} onOpenSalesFlow={this.handleOpenSalesFlow} />
                <div className="self-start mt-3">
                  <AdminRestricted>
                    <PlainTextButton text={this.props.t("calendar.import.cta")} onClick={() => window.location.href = '/settings'}/>
                  </AdminRestricted>
                  </div>
                </div>
              </>
            </Card>
            <Card className="flex-1 md:flex-none flex flex-col min-h-0">
              <>
                <div id="timezone">
                  <div className="flex items-end space-x-3">
                    <Subtitle text={settings.timezone || 'UTC'} />
                    <AdminRestricted>
                      <PlainTextButton text={this.props.t("calendar.timezone.change")} onClick={() => window.location.href = '/settings'}/>
                    </AdminRestricted>
                  </div>
                </div>
                <div className="calendar-body flex-1 md:flex-none min-h-0 overflow-hidden">
                  <FullCalendar
                    longPressDelay={0}
                    plugins={[interactionPlugin, dayGridPlugin, timeGridPlugin, momentTimezonePlugin, listPlugin]}
                    ref={this.calendarRef}
                    events={events}
                    firstDay={1}
                    eventContent={e => this.eventContent(e, this.calendarRef.current.getApi().view.type)}
                    headerToolbar={{
                      left: "prev,next today refresh",
                      center: "title",
                      right: "dayGridMonth,timeGridWeek,timeGridDay,listDay block",
                    }}
                    views={{
                      listDay: {
                        type: 'list',
                        duration: { days: 1 },
                        buttonText: this.props.t("global.list")!,
                        listDayFormat: { weekday: 'long', month: 'short', day: 'numeric' },
                        listDaySideFormat: { hour: 'numeric', minute: '2-digit' }
                      }
                    }}
                    customButtons={{
                      today: {
                        text: `${this.props.t("global.today")}`,
                        click: this.handleToday,
                      },
                      block: {
                        text: this.getButtonContent('block'),
                        click: this.handleNewButtonClick,
                      },
                      refresh: {
                        text: this.getButtonContent('refresh'),
                        click: this.handleRefresh,
                      },
                    }}
                    buttonText={
                      {
                        today:    this.props.t("global.today")!,
                        month:    this.props.t("global.month")!,
                        week:     this.props.t("global.week")!,
                        day:      this.props.t("global.day")!,
                        listDay:  this.props.t("global.list")!,
                      }
                    }
                    initialView={view}
                    nowIndicator
                    selectable={true}
                    select={this.handleDateClick}
                    eventClick={this.handleEventClick}
                    datesSet={this.handleDatesSet}
                    businessHours={mapBusinessHours(settings)}
                    // businessHours={{
                    //   daysOfWeek: [ 1, 2, 3, 4, 5, 6 ],
                    //   startTime: settings.openingHours,
                    //   endTime: settings.closingHours,
                    // }}
                    slotMinTime="06:00:00"
                    dayMaxEvents={50}
                    timeZone={settings.timezone}
                    locale={this.props.i18n.language}
                    contentHeight="auto"
                    handleWindowResize={true}
                    // TODO: leads to loop of API requests
                    // validRange={{
                    //     start: '2023-08-29'
                    //   }
                    // }
                  />
                </div>
                {/* Desktop: Legend and removed meetings button inside card */}
                <div className="hidden md:block">
                <br/>
                <div>
                  <PlainTextButton
                    text={`${this.props.t("calendar.removed_meetings.cta")}`}
                    onClick={this.fetchRemovedMeetings}
                  />
                </div>
                <Legend />
                </div>
              </>
            </Card>
            {/* Mobile: Legend and removed meetings button outside card */}
            <div className="md:hidden">
              <div>
                <PlainTextButton
                  text={`${this.props.t("calendar.removed_meetings.cta")}`}
                  onClick={this.fetchRemovedMeetings}
                />
              </div>
              <Legend />
            </div>
          </div>
          {overlapModal}
          {selectedEvent && !showOverlapWarning && (
            <MeetingFormWrapper 
              meeting={selectedEvent} 
              onMeetingChange={e => this.setState({ selectedEvent: e })} 
              actionButton={actionButton(this.state.action)} 
              isDisabled={action === 'READ'} 
              timeZone={settings.timezone}
              modalTitle={selectedEvent.customerName || (this.props.t("calendar.new_meeting") as string)}
              onClose={() => this.closeModal()}
            />
          )}
          {showBlockedDates && (
            <Modal modalTitle={"calendar.blocked_dates.form.new_dates"} canClose onClose={() => this.setState({ showBlockedDates: false})}>
              <BlockedDatesModal
                onClose={this.refreshAfterBlockDates}
                defaultDate={this.calendarRef.current?.getApi?.()?.getDate() ?? new Date()}
                timeZone={settings.timezone}
              />
            </Modal>
          )}
          {this.state.showExternalBookingConfirmation && (
            <Modal
              modalTitle={this.props.t("global.confirmation_popup.title")}
              canClose
              onClose={this.handleCancelBookingDeletion}
            >
              <div className="flex flex-col justify-between min-h-[150px]">
                <p>{ this.props.t("calendar.booking.confirmation_removal") }</p>
                <div className="flex justify-center space-x-3">
                  <DangerPrimaryButton text={this.props.t("global.confirm")!} fn={this.handleConfirmBookingDeletion} />
                  <SecondaryButton text={this.props.t("global.decline")!} fn={this.handleCancelBookingDeletion} />
                </div>
              </div>
            </Modal>
          )}
          {this.state.showExternalBookingUpdateConfirmation && (
            <Modal
              modalTitle={this.props.t("global.confirmation_popup.title")}
              canClose
              onClose={this.handleCancelBookingUpdate}
            >
              <div className="flex flex-col justify-between min-h-[150px]">
                <p>{ this.props.t("calendar.booking.confirmation_update") }</p>
                <div className="flex justify-center space-x-3">
                  <DangerPrimaryButton
                    text={this.props.t("global.confirm")!}
                    fn={this.handleConfirmBookingUpdate}
                  />
                  <SecondaryButton
                    text={this.props.t("global.decline")!}
                    fn={this.handleCancelBookingUpdate}
                  />
                </div>
              </div>
            </Modal>
          )}
          {showRemovedMeetingsModal && (
            <Modal
              modalTitle={this.props.t("calendar.removed_meetings.title")}
              canClose
              onClose={this.closeRemovedMeetingsModal}
            >
              <div className="max-h-[60vh] overflow-y-auto pr-2">
                {removedMeetings.length > 0 ? (
                  <ul className="space-y-4">
                    {removedMeetings.map((meeting) => (
                      <li key={meeting.id} className="bg-muted p-4 rounded-lg shadow-sm">
                        <div className="flex items-center space-x-3">
                          <User className="text-primary h-5 w-5" />
                          <span className="font-medium">
                            {`${meeting.customerName} ${meeting.customerSurname ?? ''}`}
                          </span>
                        </div>
                        <div className="mt-2 flex items-center space-x-3 text-sm text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          <span>{`${this.props.t(meetingTypeToTranslationKey(meeting.type))} (${parseDateTime(meeting.startTime).date} - ${parseDateTime(meeting.endTime).time})`}</span>
                        </div>
                        { meeting.phone ? <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                          <Phone className="h-4 w-4" />
                          <span className="font-medium">
                            {`${meeting.phone}`}
                          </span>
                        </div> : null }
                        { meeting.email ? <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                          <Mail className="h-4 w-4" />
                          <span className="font-medium">
                            {`${meeting.email}`}
                          </span>
                        </div> : null }
                        { meeting.notes ? <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                          <Pencil className="h-4 w-4" />
                          <span className="font-medium">
                            {`${meeting.notes}`}
                          </span>
                        </div> : null }
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-center text-muted-foreground">
                    {this.props.t("calendar.removed_meetings.empty")}
                  </p>
                )}
              </div>
            </Modal>
          )}
          {this.state.showSmsConfirmModal && this.state.smsPendingForSend && (
            <SmsConfirmModal
              bodyTranslationKey={
                this.state.smsPendingForSend.useCase === 'cancelation'
                  ? 'calendar.sms_confirm.body.delete'
                  : 'calendar.sms_confirm.body.create_update'
              }
              onYes={this.handleSmsConfirmYes}
              onNo={this.handleSmsConfirmNo}
              onClose={() => this.setState({ showSmsConfirmModal: false, smsPendingForSend: null })}
            />
          )}
          {this.state.showSmsLimitModal && (
            <SmsLimitReachedModal onClose={this.handleSmsLimitClose} />
          )}
          {/* Sales Flow Sidebar */}
          <SlideOver
            isOpen={this.state.showSalesFlow}
            setIsOpen={(open: boolean) => {
              if (!open) {
                this.handleCloseSalesFlow();
              } else {
                this.setState({ showSalesFlow: open });
              }
            }}
            title={this.props.t("sales.form.create_title") || "Create Sale"}
            isLoading={false}
            hasErrors={this.state.salesFlowNavigation ? !this.state.salesFlowNavigation.canProceed() : false}
            secondaryButton={
              this.state.salesFlowNavigation && this.state.salesFlowNavigation.currentStep > 1
                ? {
                    label: this.props.t("sales.form.back") || "Back",
                    onClick: () => this.state.salesFlowNavigation?.handleBack()
                  }
                : this.state.salesFlowNavigation && this.state.salesFlowNavigation.currentStep === 1
                ? {
                    label: this.props.t("sales.form.cancel") || "Cancel",
                    onClick: this.handleCloseSalesFlow
                  }
                : undefined
            }
            primaryButton={
              this.state.salesFlowNavigation
                ? {
                    label: this.state.salesFlowNavigation.buttonLabel || 
                      (this.state.salesFlowNavigation.currentStep < this.state.salesFlowNavigation.totalSteps
                        ? (this.props.t("sales.form.next") || "Next")
                        : (this.props.t("sales.form.complete") || "Complete")),
                    onClick: () => {
                      const nav = this.state.salesFlowNavigation;
                      // Always delegate to the stepper on the last step too.
                      // Step 6 contains important side-effects (e.g. contract PDF generation)
                      // and will call `onComplete` when it's actually time to close.
                      if (nav) {
                        nav.handleNext();
                        return;
                      }
                      this.handleCompleteSale();
                    }
                  }
                : undefined
            }
          >
            {this.state.currentSale && (
              <SaleFormSteps
                sale={this.state.currentSale}
                onSaleChange={this.handleSaleChange}
                onComplete={this.handleCompleteSale}
                onCancel={this.handleCloseSalesFlow}
                onNavigationChange={this.handleSalesFlowNavigationChange}
              />
            )}
          </SlideOver>
        </>
      </PageWrapper>
    );
  }
}

const connectedCalendarPage = connect(mapStateToProps)(withTranslation()(CalendarPageWrapper));

export default connectedCalendarPage;
